import cx from "classnames";
import { type FC, type MouseEventHandler, useEffect, useState } from "react";

export interface ResizeOffset {
	x: number;
	y: number;
}

export type ResizeDisplayComponent = FC<{
	onMouseDown: MouseEventHandler<HTMLButtonElement>;
	isMouseDown: boolean;
}>;

export const XResizeDisplayComponent: ResizeDisplayComponent = ({ onMouseDown, isMouseDown }) => (
	<div className="relative">
		<button
			className={cx(
				"absolute",
				"flex",
				"h-full",
				"select-none",
				"flex-row",
				"items-stretch",
				"z-draggable"
			)}
			onMouseDown={onMouseDown}
			style={{ width: "16px", left: "-8px", cursor: "col-resize" }}
			data-resizer
			tabIndex={-1}
		>
			<div className={`mx-auto ${isMouseDown ? "bg-brand" : ""}`} style={{ width: "3px" }} />
		</button>
	</div>
);

export const YResizeDisplayComponent: ResizeDisplayComponent = ({ onMouseDown, isMouseDown }) => (
	<button
		className={cx(
			"absolute",
			"flex",
			"w-full",
			"select-none",
			"flex-col",
			"items-stretch",
			"z-draggable"
		)}
		onMouseDown={onMouseDown}
		style={{ height: "16px", top: "-10px", cursor: "row-resize" }}
		tabIndex={-1}
	>
		<div className={`my-auto ${isMouseDown ? "bg-brand" : ""}`} style={{ height: "3px" }} />
	</button>
);

export interface ResizeBorderProps {
	onResizeMove: (offset: ResizeOffset) => void;
	onResizeStart: () => void;
	onResizeEnd?: () => void;
	borderDisplayComponent: ResizeDisplayComponent;
}

export const ResizeBorder: FC<ResizeBorderProps> = ({
	onResizeMove,
	onResizeStart,
	onResizeEnd,
	borderDisplayComponent: BorderDisplayComponent,
}) => {
	const [isMouseDown, setIsMouseDown] = useState(false);
	const [startOffset, setStartOffset] = useState<ResizeOffset>({ x: 0, y: 0 });

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (isMouseDown) {
				onResizeMove({
					x: startOffset.x - e.clientX,
					y: startOffset.y - e.clientY,
				});
			}
		};

		window.addEventListener("mousemove", handleMouseMove);

		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
		};
	}, [isMouseDown, onResizeMove]);

	useEffect(() => {
		const handleMouseUp = () => {
			onResizeEnd?.();
			setIsMouseDown(false);
		};

		window.addEventListener("mouseup", handleMouseUp);

		return () => {
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, []);

	const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
		setIsMouseDown(true);
		setStartOffset({ x: e.clientX, y: e.clientY });
		onResizeStart();
	};

	return <BorderDisplayComponent onMouseDown={handleMouseDown} isMouseDown={isMouseDown} />;
};
